import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import type { DateRange } from 'react-day-picker';
import dayjs from 'dayjs';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  Pencil,
  Plus,
  RotateCw,
  Trash2,
  Upload,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DateRangePicker } from '@/components/shared/DateRangePicker';
import type { AppShellContext } from '@/components/layout/AppShell';
import { UploadSignedDialog } from './UploadSignedDialog';
import { useWarehouseApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useWzFilters } from '@/hooks/useWzFilters';
import { hasCompanyRole } from '@/lib/permissions';
import { t } from '@/i18n';
import { WZ_STATUSES, type WzListItem } from '@ip/shared-types';

const COLUMN_COUNT = 6;
const PAGE_SIZE = 20;

function WzStatusBadge({ status }: { status: WzListItem['status'] }) {
  if (status === 'returned') {
    return (
      <Badge className="border-transparent bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
        {t('magazyn.status.returned')}
      </Badge>
    );
  }
  return <Badge variant="secondary">{t('magazyn.status.issued')}</Badge>;
}

/**
 * WZ list — orchestration mirrors InvoiceList (loadSeq ref-guard against
 * out-of-order responses): filter bar (search, date range defaulting to the
 * current month, status), "Nowe WZ" + "Wgraj podpisany skan", a table with
 * per-row PDF download / edit / delete, and page navigation. No prices appear
 * anywhere — the WZ surface never carries them.
 */
export function WzList() {
  const { nip } = useOutletContext<AppShellContext>();
  const navigate = useNavigate();
  const { permissions, isSuperAdmin } = useAuth();
  const { listWz, downloadWzPdf, deleteWz } = useWarehouseApi();

  const {
    from,
    to,
    status,
    search,
    page,
    setDateRange,
    setStatus,
    setSearch,
    setPage,
    clearAllFilters,
    hasActiveFilters,
  } = useWzFilters();

  const [items, setItems] = useState<WzListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WzListItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const loadSeq = useRef(0);

  // Returned documents already carry a signed scan; only a company admin may
  // edit/delete them (mirrors the backend checkCompanyRole(nip, ['admin'])).
  const canManageReturned = isSuperAdmin || hasCompanyRole(permissions, nip, ['admin']);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const result = await listWz(nip, {
        from: from || undefined,
        to: to || undefined,
        status,
        search: search || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      if (seq !== loadSeq.current) return;
      setItems(result.items);
      setTotal(result.total);
      setForbidden(false);
    } catch (error) {
      if (seq !== loadSeq.current) return;
      if ((error as { status?: number })?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(t('errors.loadingFailed'));
      }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [nip, from, to, status, search, page, listWz]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const pickerRange: DateRange | undefined = useMemo(() => {
    if (!from && !to) return undefined;
    return {
      from: from ? dayjs(from).toDate() : undefined,
      to: to ? dayjs(to).toDate() : undefined,
    };
  }, [from, to]);

  const handleDownload = useCallback(
    async (wz: WzListItem) => {
      setDownloadingId(wz.id);
      try {
        const blob = await downloadWzPdf(nip, wz.id);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${wz.number.replace(/\//g, '_')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch {
        toast.error(t('magazyn.list.downloadFailed'));
      } finally {
        setDownloadingId(null);
      }
    },
    [nip, downloadWzPdf],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteWz(nip, deleteTarget.id);
      toast.success(t('magazyn.list.deleteSuccess'));
      setDeleteTarget(null);
      load();
    } catch {
      toast.error(t('magazyn.list.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, nip, deleteWz, load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (forbidden) {
    return <p className="text-muted-foreground py-10 text-center">{t('errors.accessDenied')}</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => setUploadOpen(true)}>
          <Upload className="size-4" />
          {t('magazyn.list.uploadSigned')}
        </Button>
        <Button size="sm" onClick={() => navigate('wz/new')}>
          <Plus className="size-4" />
          {t('magazyn.list.newWz')}
        </Button>
      </div>

      <Card className="flex flex-wrap items-end gap-3 p-3">
        <div className="min-w-[220px] flex-1">
          <p className="text-muted-foreground mb-1 text-xs">{t('common.search')}</p>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('magazyn.list.searchPlaceholder')}
          />
        </div>
        <div>
          <p className="text-muted-foreground mb-1 text-xs">{t('magazyn.list.columns.issueDate')}</p>
          <DateRangePicker value={pickerRange} onChange={setDateRange} />
        </div>
        <div>
          <p className="text-muted-foreground mb-1 text-xs">{t('magazyn.list.columns.status')}</p>
          <Select
            value={status ?? 'all'}
            onValueChange={(v) => setStatus(v === 'all' ? undefined : (v as WzListItem['status']))}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('magazyn.list.statusAll')}</SelectItem>
              {WZ_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`magazyn.status.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters}>
            {t('magazyn.list.clearFilters')}
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={load} aria-label={t('common.refreshNow')}>
          <RotateCw className="size-4" />
        </Button>
      </Card>

      <div className="bg-card overflow-x-auto rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>{t('magazyn.list.columns.number')}</TableHead>
              <TableHead>{t('magazyn.list.columns.issueDate')}</TableHead>
              <TableHead>{t('magazyn.list.columns.buyer')}</TableHead>
              <TableHead>{t('magazyn.list.columns.deliveryMethod')}</TableHead>
              <TableHead>{t('magazyn.list.columns.status')}</TableHead>
              <TableHead className="w-[140px]">{t('magazyn.list.columns.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 8 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="h-40 text-center">
                  <p className="font-medium">{t('magazyn.list.empty.title')}</p>
                  <p className="text-muted-foreground text-sm">{t('magazyn.list.empty.description')}</p>
                </TableCell>
              </TableRow>
            ) : (
              items.map((wz) => {
                const canDelete = wz.status === 'issued' || canManageReturned;
                return (
                  <TableRow key={wz.id}>
                    <TableCell className="font-medium">{wz.number}</TableCell>
                    <TableCell className="whitespace-nowrap">{wz.issueDate}</TableCell>
                    <TableCell className="max-w-[220px] truncate" title={wz.contractorName}>
                      {wz.contractorName}
                    </TableCell>
                    <TableCell>
                      {wz.trackingNumber ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="underline decoration-dotted underline-offset-2">
                              {t(`magazyn.deliveryMethods.${wz.deliveryMethod}`)}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {t('magazyn.list.columns.trackingNumber')}: {wz.trackingNumber}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        t(`magazyn.deliveryMethods.${wz.deliveryMethod}`)
                      )}
                    </TableCell>
                    <TableCell>
                      <WzStatusBadge status={wz.status} />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('magazyn.list.downloadPdf')}
                          disabled={downloadingId === wz.id}
                          onClick={() => handleDownload(wz)}
                        >
                          <Download className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('magazyn.list.edit')}
                          onClick={() => navigate(`wz/${wz.id}`)}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-destructive hover:text-destructive"
                            aria-label={t('magazyn.list.delete')}
                            onClick={() => setDeleteTarget(wz)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t px-3 py-2">
          <p className="text-muted-foreground text-sm">
            {t('pagination.showing', {
              from: total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1,
              to: Math.min(page * PAGE_SIZE, total),
              total,
            })}
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="size-8"
              aria-label={t('pagination.firstPage')}
              disabled={page <= 1}
              onClick={() => setPage(1)}
            >
              <ChevronsLeft className="size-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="size-8"
              aria-label={t('pagination.prevPage')}
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <span className="px-2 text-sm tabular-nums">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="size-8"
              aria-label={t('pagination.nextPage')}
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              <ChevronRight className="size-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="size-8"
              aria-label={t('pagination.lastPage')}
              disabled={page >= totalPages}
              onClick={() => setPage(totalPages)}
            >
              <ChevronsRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>

      <UploadSignedDialog nip={nip} open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={load} />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('magazyn.list.deleteConfirmTitle', { number: deleteTarget?.number ?? '' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('magazyn.list.deleteConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={deleting} variant="destructive" onClick={handleDelete}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
